import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { me, login as apiLogin, logout as apiLogout, formatApiError } from "../lib/api";

const AuthContext = createContext({
  user: null,             // null = loading, false = anonymous, object = authenticated
  refresh: () => {},
  login: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const u = await me();
      setUser(u);
    } catch {
      // Anonymous (no/expired cookie) is the expected state for unauthenticated
      // visitors. Surfacing this as an error would spam the console on every
      // public page load, so we deliberately swallow it and mark the user as
      // anonymous (false). Real network errors will still surface in the
      // network panel and via axios interceptors.
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const u = await apiLogin(email, password);
      setUser(u);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (e) {
      // Server may already have rotated the session. We always clear local
      // state regardless so the UI returns to anonymous.
      console.warn("logout: server-side logout failed (clearing client state anyway)", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, refresh, login, logout }),
    [user, refresh, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
